import { useEffect, useRef } from 'react'

import { useNavigationPath, useNavigationStore, useOpenAccordions } from '@/core/stores/navigationStore'

const MAX_SCROLL_ATTEMPTS = 30
const SCROLL_RETRY_DELAY_MS = 16

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement
  while (current && current !== document.body) {
    const overflowY = window.getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

export function useScrollToMethod() {
  const path = useNavigationPath()
  const openAccordions = useOpenAccordions()
  const pathChangedByNavigation = useNavigationStore((state) => state.pathChangedByNavigation)
  const isAtMemberLevel = path.length === 3
  const memberId = path[2]
  const isMemberAccordionOpen = Boolean(memberId && openAccordions.includes(memberId))
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollRafRef = useRef<number | null>(null)
  const scrollAttemptsRef = useRef<number>(0)

  useEffect(() => {
    const clearPendingScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
        scrollTimeoutRef.current = null
      }
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
    }

    if (!isAtMemberLevel || !memberId || !pathChangedByNavigation) {
      clearPendingScroll()
      scrollAttemptsRef.current = 0
      return
    }

    clearPendingScroll()
    scrollAttemptsRef.current = 0

    const findAnchorElement = (id: string): HTMLElement | null =>
      document.getElementById(`anchor-${encodeURIComponent(id)}`)

    const attemptScroll = () => {
      scrollAttemptsRef.current += 1
      const anchorElement = findAnchorElement(memberId)

      if (!isMemberAccordionOpen || !anchorElement) {
        if (scrollAttemptsRef.current < MAX_SCROLL_ATTEMPTS) {
          scrollTimeoutRef.current = setTimeout(attemptScroll, SCROLL_RETRY_DELAY_MS)
          return
        }
        useNavigationStore.getState().resetNavigationFlag()
        return
      }

      scrollRafRef.current = requestAnimationFrame(() => {
        const scrollContainer = findScrollContainer(anchorElement)
        const topbarHeight = parseFloat(window.getComputedStyle(anchorElement).scrollMarginTop) || 0

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect()
          const anchorRect = anchorElement.getBoundingClientRect()
          const offsetWithinContainer = anchorRect.top - containerRect.top + scrollContainer.scrollTop
          const usableHeight = Math.max(0, scrollContainer.clientHeight - topbarHeight)
          const desiredTopWithinContainer = topbarHeight + Math.max(0, (usableHeight - anchorRect.height) / 2)
          const target = Math.max(0, offsetWithinContainer - desiredTopWithinContainer)
          scrollContainer.scrollTo({ top: target, behavior: 'smooth' })
        } else {
          anchorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        useNavigationStore.getState().resetNavigationFlag()
      })
    }

    scrollTimeoutRef.current = setTimeout(attemptScroll, SCROLL_RETRY_DELAY_MS)

    return () => {
      clearPendingScroll()
      scrollAttemptsRef.current = 0
    }
  }, [isAtMemberLevel, memberId, isMemberAccordionOpen, pathChangedByNavigation])
}
